import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import type {
  IceHockeyFixture,
  IceHockeyPeriod,
  IceHockeyPeriodScore
} from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  iceHockeyFixture?: IceHockeyFixture;
}

const standardPeriods = ['PERIOD_1', 'PERIOD_2', 'PERIOD_3'] as const satisfies IceHockeyPeriod[];

const isIceHockeyPeriodScore = (
  periodScore: IceHockeyPeriodScore | null
): periodScore is IceHockeyPeriodScore =>
  notEmpty(periodScore) && notEmpty(periodScore.period) && notEmpty(periodScore.score);

const getPeriodScores = (
  periodScores: IceHockeyFixture['periodScores']
): IceHockeyPeriodScore[] => {
  const standardPeriodScores = standardPeriods.map(
    (period) => periodScores?.find((ps) => ps?.period === period) ?? { period }
  );

  const additionalPeriodScores =
    periodScores?.filter((ps) => !standardPeriodScores.find((sp) => sp.period === ps?.period)) ??
    [];

  return [...standardPeriodScores, ...additionalPeriodScores.filter(isIceHockeyPeriodScore)];
};

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: IceHockeyFixture['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const IceHockey: React.FC<Props> = ({
  enrichedParticipants,
  iceHockeyFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];

  const currentScore = participantA.type && participantB.type ? iceHockeyFixture?.score : null;
  const periodScores =
    participantA.type && participantB.type ? iceHockeyFixture?.periodScores : null;

  const hasPeriodScores = notEmpty(periodScores);
  const scores = getPeriodScores(periodScores);

  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.ICE_HOCKEY}>
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant name={participantA.participant} />
          <ScoreboardParticipant name={participantB.participant} />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {hasPeriodScores
            ? scores.map((score) => (
                <ScoreboardScore
                  key={score.period}
                  score={[
                    getParticipantScore(participantA, score.score) ?? null,
                    getParticipantScore(participantB, score.score) ?? null
                  ]}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentScore) ?? null,
              getParticipantScore(participantB, currentScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.ICE_HOCKEY, [
              currentScore?.home ?? 0,
              currentScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default IceHockey;
